import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { breadcrumbList, faqPage, jsonLdScript, webPage, type FaqEntry } from '@/lib/schema'

const URL = 'https://uptrack.app/cron-monitoring'
const TITLE = 'Cron Job Monitoring with Heartbeats | Uptrack'
const DESCRIPTION =
  'Find out when a cron job stops running. Your job pings a URL after each run, and Uptrack alerts you when a ping is late. Needs a paid plan, from $3/mo.'

const PING_URL = 'https://api.uptrack.app/api/heartbeat/YOUR_TOKEN'

const faq: FaqEntry[] = [
  {
    question: 'What is cron job monitoring?',
    answer:
      'Cron job monitoring tells you when a scheduled job did not run. The job sends a small request, called a heartbeat or ping, after each successful run. The monitor expects that ping on a schedule. When a ping is late, you get an alert.',
  },
  {
    question: 'Does cron job monitoring work on every plan?',
    answer:
      'No. Heartbeat monitors need a paid plan. Paid plans start at $3/mo and have a 30-day money-back guarantee. Heartbeat monitors count against the monitor limit of your plan and have no extra fee.',
  },
  {
    question: 'How late can a ping be before Uptrack alerts me?',
    answer:
      'A new heartbeat monitor expects a ping every 1 hour (3600 seconds) with a grace period of 5 minutes (300 seconds). Uptrack alerts when the last ping is older than the interval plus the grace period. It looks for late pings once a minute. To change the two values, send expected_interval_seconds and grace_period_seconds in the settings when you create the monitor with the API.',
  },
  {
    question: 'What happens if the job runs but fails?',
    answer:
      'Uptrack only knows whether a ping arrived. It does not read the exit code or the logs of your job. Send the ping only after the real work succeeded, for example with && in a shell script. A failed job then sends no ping and the monitor alerts.',
  },
  {
    question: 'Will I get an alert for a monitor that never received a ping?',
    answer:
      'No. A heartbeat monitor starts to watch after its first ping. Send one ping when you set the job up, for example by running the curl command by hand.',
  },
  {
    question: 'Which requests does the ping URL accept?',
    answer:
      'GET, POST and HEAD. A POST can carry a JSON body with the optional fields execution_time, status and message. A GET can carry the same fields as query parameters. An unknown token returns 404.',
  },
  {
    question: 'Is the ping URL a secret?',
    answer:
      'Yes. The token in the URL is the only credential, so anyone who has the URL can send a ping and keep the monitor quiet. Keep it out of public repositories and logs.',
  },
]

export const Route = createFileRoute('/_site/cron-monitoring')({
  component: CronMonitoringPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: TITLE },
      { property: 'og:description', content: DESCRIPTION },
      { property: 'og:url', content: URL },
      { property: 'og:image', content: 'https://uptrack.app/og/cron-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: URL }],
    scripts: [
      jsonLdScript(
        breadcrumbList([
          { name: 'Home', url: 'https://uptrack.app' },
          { name: 'Cron job monitoring', url: URL },
        ]),
      ),
      jsonLdScript(webPage({ name: TITLE, description: DESCRIPTION, url: URL })),
      jsonLdScript(faqPage(faq)),
    ],
  }),
})

const steps = [
  { title: 'Create a heartbeat monitor', desc: 'Pick "Heartbeat / Cron" as the monitor type. Uptrack gives the monitor a unique ping URL.' },
  { title: 'Add the ping to your job', desc: 'Call the URL when the job finishes. Use && so the ping goes out only after the job succeeded.' },
  { title: 'Send one ping by hand', desc: 'The monitor starts to watch after its first ping. Run the curl command once to start it.' },
]

const timing = [
  { setting: 'expected_interval_seconds', value: '3600 (1 hour)', meaning: 'How often a ping should arrive.' },
  { setting: 'grace_period_seconds', value: '300 (5 minutes)', meaning: 'Extra time before Uptrack counts the ping as missed.' },
]

const notForYou = [
  'You need to know why a job failed. Uptrack sees a ping or no ping. It does not read exit codes, logs or job output.',
  'You need a start signal, to tell a job that never started from one that hangs. Heartbeat monitors have the one ping only.',
  'You only need cron monitoring and nothing else. Dedicated tools such as Healthchecks.io and Cronitor are built for it. Better Stack lists 10 heartbeats on its free plan (checked 2026-10-12).',
  'You need heartbeat monitors without paying. They need a paid plan, from $3/mo.',
]

const useCases = [
  { title: 'Database backups', description: 'A nightly pg_dump or mysqldump that did not run leaves you with no recent backup.' },
  { title: 'Data pipelines', description: 'A broken import or export shows up as stale dashboards, often days later.' },
  { title: 'Queue workers', description: 'A worker can ping from a timer inside its loop, so a stalled worker goes quiet and you hear about it.' },
  { title: 'Scheduled reports and billing', description: 'A weekly email or a monthly billing run that is skipped is easy to miss.' },
]

const H2 = 'text-2xl font-semibold tracking-tight md:text-3xl'
const TH = 'px-4 py-3 text-left text-sm font-semibold'
const TD = 'px-4 py-3 text-sm text-body'

function CronMonitoringPage() {
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">Find out when a cron job stops running</h1>
            <p className="mt-5 max-w-2xl text-lg text-body">
              Cron sends no alert when a job fails to start, crashes or hangs. With a heartbeat monitor, your job pings a URL after each run. When a
              ping is late, Uptrack alerts you by email, Slack, Discord, Telegram or webhook.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Heartbeat monitors need a paid plan, from <span className="num">$3</span>/mo · 30-day money-back guarantee on paid plans
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto grid gap-10 px-4 py-14 md:py-20 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className={H2}>How a heartbeat monitor works</h2>
            <p className="mt-3 max-w-xl text-body">
              The job reports in, so the monitor can tell when it stops. This also catches the case where the whole server is down, because no ping
              arrives. There is no agent to install.
            </p>
          </div>
          <div className="min-w-0 space-y-6 lg:col-span-7">
            <ol className="divide-y divide-border border-y border-border">
              {steps.map((st, i) => (
                <li key={st.title} className="flex gap-4 py-5">
                  <span className="num w-5 text-sm text-muted-foreground">{i + 1}</span>
                  <div>
                    <h3 className="font-medium">{st.title}</h3>
                    <p className="mt-1 text-body">{st.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="mb-3 text-sm text-body">A crontab line. The ping runs only if the backup script exits with 0:</p>
              <pre tabIndex={0} className="overflow-x-auto font-mono text-sm leading-relaxed">
                <code>{`0 2 * * * /usr/local/bin/backup.sh && curl -fsS -m 10 --retry 3 -o /dev/null ${PING_URL}`}</code>
              </pre>
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="mb-3 text-sm text-body">A POST with the optional fields. A GET takes the same fields as query parameters:</p>
              <pre tabIndex={0} className="overflow-x-auto font-mono text-sm leading-relaxed">
                <code>{`curl -fsS -X POST -H 'Content-Type: application/json' \\
  -d '{"status":"ok","message":"backup finished"}' \\
  ${PING_URL}`}</code>
              </pre>
            </div>
            <p className="text-body">
              Copy the exact URL from the page of your monitor. It has the token of that monitor in place of <code className="font-mono text-sm">YOUR_TOKEN</code>.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <h2 className={H2}>When Uptrack alerts you</h2>
          <p className="mt-3 max-w-2xl text-body">
            Uptrack alerts when the last ping is older than the expected interval plus the grace period. The timer starts at the last ping, not at the
            time on your schedule. Uptrack looks for late pings once a minute.
          </p>
          <div className="mt-6 overflow-x-auto rounded-lg border border-border" tabIndex={0} role="region" aria-label="Heartbeat settings">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className={TH}>Setting</th>
                  <th className={TH}>Default</th>
                  <th className={TH}>What it does</th>
                </tr>
              </thead>
              <tbody>
                {timing.map((row) => (
                  <tr key={row.setting} className="border-b border-border last:border-0">
                    <td className={`${TD} font-mono`}>{row.setting}</td>
                    <td className={`${TD} num`}>{row.value}</td>
                    <td className={TD}>{row.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 max-w-2xl text-body">
            Example: a nightly job takes up to 20 minutes. Set the interval to 86400 seconds and the grace period to 1800 seconds. You hear about it
            when no ping arrives within 24 hours and 30 minutes of the last one.
          </p>
          <ul className="mt-6 max-w-2xl list-disc space-y-2 pl-6 text-body">
            <li>One late job opens one incident. You do not get a new alert every minute.</li>
            <li>The next ping closes the incident by itself.</li>
            <li>A paused monitor sends no alert, and a monitor with no ping yet sends none either.</li>
          </ul>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <h2 className={H2}>What you can watch</h2>
          <p className="mt-3 max-w-2xl text-body">Anything that runs on a schedule and can send one HTTP request when it is done.</p>
          <ul className="mt-8 divide-y divide-border border-y border-border">
            {useCases.map((uc) => (
              <li key={uc.title} className="grid gap-x-8 gap-y-1 py-4 md:grid-cols-12">
                <h3 className="font-medium md:col-span-3">{uc.title}</h3>
                <p className="text-body md:col-span-9">{uc.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <h2 className={H2}>Heartbeat monitoring is not for you if</h2>
          <ul className="mt-6 max-w-3xl list-disc space-y-3 pl-6 text-body">
            {notForYou.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="mt-6 max-w-3xl text-body">
            See how Uptrack compares with{' '}
            <Link to="/compare/healthchecks" className="underline">
              Healthchecks.io
            </Link>{' '}
            and{' '}
            <Link to="/compare/cronitor" className="underline">
              Cronitor
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto max-w-3xl px-4 py-14 md:py-20">
          <h2 className={H2}>Frequently asked questions</h2>
          <div className="mt-8 space-y-6">
            {faq.map((entry) => (
              <div key={entry.question}>
                <h3 className="font-medium">{entry.question}</h3>
                <p className="mt-1 text-body">{entry.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="grid items-center gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className={H2}>Watch your first cron job</h2>
              <p className="mt-3 max-w-lg text-body">
                Heartbeat monitors share the monitor limit of your plan, with no extra fee. Read the{' '}
                <Link to="/features/heartbeat-monitoring" className="underline">
                  heartbeat monitoring feature page
                </Link>
                , or see{' '}
                <Link to="/free-uptime-monitoring" className="underline">
                  free uptime monitoring
                </Link>{' '}
                for websites and APIs.
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <p className="mt-3 text-sm text-muted-foreground">
                from <span className="num">$3</span>/mo · 30-day money-back guarantee on paid plans
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
